import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useRef } from 'react';

import { useAppServices } from '@/app/AppServicesContext';
import { BLACK_HOLE_SIDE, type ChapterKey, sideSign } from '@/core/chapters';
import { easeInOutSine } from '@/core/math';
import { useContent } from '@/content/ContentContext';
import { useCosmos } from '@/features/cosmos/CosmosContext';
import { MOTION_ALLOWED } from '@/motion/queries';

import styles from './Crossing.module.css';

/**
 * Repères sur l'échelle de la traversée (de 0, quand elle atteint le milieu de
 * l'écran, à 1, quand elle s'achève) : la carte arrive après la vitesse de la
 * lumière et reste jusqu'à l'arrivée du chapitre.
 */
const CARD_TIMING = {
  full: { enter: 0.6, settled: 0.72 },
  emerge: { enter: 0.15, settled: 0.32 },
} as const;

/**
 * Pilote automatique : dès qu'on s'engage dans la traversée, le site fait le
 * trajet et s'arrête sur la carte du chapitre, le nouveau trou noir en place.
 * Le visiteur reprend ensuite la main ; en remontant, il revient au chapitre précédent.
 */
const AUTOPILOT = {
  /** Durée du trajet, en secondes : on prend le temps de regarder. */
  duration: { full: 4.6, emerge: 4.6 },
  /**
   * Après le hero, le trajet commence avec sa plongée : le pilote prend la main
   * dès la fin du dernier temps fort (« Impact »), en avancée du hero.
   */
  heroHandoff: 0.8,
  /** Arrêt en descente : la carte est lisible, le trou noir presque en place. */
  arrival: { full: 0.9, emerge: 0.85 },
  /** Fenêtres d'engagement : on ne repart pas quand on reprend la main. */
  engage: 0.02,
  downUntil: 0.3,
  upFrom: 0.7,
  /** Arrêt en remontée, en hauteur d'écran : le chapitre précédent revient à l'écran. */
  backoff: 0.1,
} as const;

interface CrossingProps {
  /** Le chapitre où l'on arrive. */
  readonly to: ChapterKey;
  /** Juste après le hero : la plongée a déjà eu lieu, on ne fait que ressortir. */
  readonly emergeOnly?: boolean;
}

/**
 * Une traversée : un passage plein écran, sans texte courant, où la caméra
 * plonge dans le trou noir du chapitre précédent et ressort près du suivant.
 * Une carte annonce le chapitre et glisse du trou noir vers le futur texte.
 * Il suffit de s'y engager : le site fait le trajet, dans un sens comme dans l'autre.
 * Sans décor 3D, la traversée n'existe pas : les chapitres s'enchaînent.
 */
export function Crossing({ to, emergeOnly = false }: CrossingProps) {
  const { journey } = useContent();
  const { device, motion } = useAppServices();
  const { status } = useCosmos();
  const rootRef = useRef<HTMLDivElement>(null);
  const active = device.canRenderCosmos && status !== 'off';
  const chapter = journey.chapters[to];
  const side = BLACK_HOLE_SIDE[to];

  useGSAP(
    () => {
      const root = rootRef.current;
      if (!active || !root) return;
      const variant = emergeOnly ? 'emerge' : 'full';
      const timing = CARD_TIMING[variant];
      const drift = sideSign(side);
      const media = gsap.matchMedia();
      media.add(MOTION_ALLOWED, () => {
        const card = root.querySelector('[data-crossing="card"]');

        // Même échelle que le décor : du milieu de l'écran à la fin de la traversée.
        const timeline = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: { trigger: root, start: 'top center', end: 'bottom bottom', scrub: 0.6 },
        });
        const passage = timeline.scrollTrigger;
        if (!passage) return;

        // Où le pilote commence à veiller : au milieu de l'écran, ou dans le hero
        // pour la première traversée (la plongée fait partie du trajet).
        const lead = (): number => {
          const hero = root.previousElementSibling;
          if (!emergeOnly || !(hero instanceof HTMLElement)) return passage.start;
          const top = hero.getBoundingClientRect().top + window.scrollY;
          return top + AUTOPILOT.heroHandoff * (hero.offsetHeight - window.innerHeight);
        };

        // Le pilote ne prend pas la main pendant un trajet déjà piloté (menu, autre traversée).
        ScrollTrigger.create({
          trigger: root,
          start: lead,
          end: 'bottom bottom',
          onUpdate: (self) => {
            if (motion.guided) return;
            const options = {
              duration: AUTOPILOT.duration[variant],
              lock: true,
              easing: easeInOutSine,
            };
            const arrival =
              passage.start + AUTOPILOT.arrival[variant] * (passage.end - passage.start);
            if (
              self.direction > 0 &&
              self.progress > AUTOPILOT.engage &&
              self.progress < AUTOPILOT.downUntil
            ) {
              motion.scrollTo(arrival, options);
            } else if (
              self.direction < 0 &&
              passage.progress > AUTOPILOT.upFrom &&
              passage.progress < 1 - AUTOPILOT.engage
            ) {
              const back = self.start - window.innerHeight * AUTOPILOT.backoff;
              motion.scrollTo(Math.max(back, 0), options);
            }
          },
        });
        timeline.set({}, {}, 1);
        timeline.fromTo(
          card,
          { autoAlpha: 0, xPercent: drift * 22, scale: 0.92, filter: 'blur(12px)' },
          {
            autoAlpha: 1,
            xPercent: 0,
            scale: 1,
            filter: 'blur(0px)',
            duration: timing.settled - timing.enter,
          },
          timing.enter,
        );
      });
      return () => {
        media.revert();
      };
    },
    { scope: rootRef, dependencies: [active, emergeOnly, side, motion] },
  );

  if (!active) return null;

  return (
    <div
      ref={rootRef}
      className={emergeOnly ? `${styles.crossing} ${styles.emerge}` : styles.crossing}
      data-cosmos-crossing={emergeOnly ? 'emerge' : 'full'}
      aria-hidden="true"
    >
      <div className={styles.stage}>
        <div className={styles.card} data-crossing="card">
          <span className={styles.label}>
            {journey.crossing} <span className={styles.index}>{chapter.index}</span>
          </span>
          <span className={styles.title}>{chapter.title}</span>
          <span className={styles.star}>
            {journey.starLabel} {chapter.star}
          </span>
          <span className={styles.coordinates}>{chapter.coordinates}</span>
        </div>
      </div>
    </div>
  );
}
